import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Chip, Paper, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { Disclose, Headline, Loading, Refusal } from "@lopie/kernel-ui";

/**
 * Where does each agent's data come from?
 *
 * ## The question nothing else here answers
 *
 * Every other screen in this product reads down one column. The Agents chart
 * says what an agent may touch; Connectors says what a connector has written;
 * Objects says what is in the graph. Nobody could look at one page and see
 * that **`receivables-review` reads invoices that `xero` brings in** — which
 * is the first thing anybody asks when they are deciding whether to let an
 * unattended agent near their books.
 *
 * So this is one picture of four columns and the paths between them:
 *
 *     connector  ──writes──>  type  ──may read──>  app  ──declares──>  agent
 *
 * ## Two of those edges are declared and one is derived, and it says so
 *
 * **`app ──declares──> agent`** is a fact. It is in a committed manifest that
 * somebody reviewed.
 *
 * **`type ──may read──> app`** is a fact. It is the app's `service_reads`,
 * which is the half in force when nobody is signed in — and an agent always
 * runs with nobody signed in, so it is the right half to draw.
 *
 * **`connector ──writes──> type`** is a fact about what has been written, out
 * of the kernel's own count of its graph.
 *
 * But the path a reader's eye actually traces — *xero feeds this agent* — is
 * **none of those three**. It is derived by joining them on a type, and it is
 * a statement about reach rather than about wiring: nothing connects xero to
 * that agent, and nobody configured a pipeline. The agent may read a type,
 * and records of that type happen to have been written by xero.
 *
 * That distinction is the whole reason this screen is allowed to exist. A
 * diagram that drew a confident line from a connector to an agent would be
 * inventing a relationship, which is exactly the overstatement this product
 * is against — so the derived hop is drawn dashed, the legend says which is
 * which, and the caption says it in words.
 *
 * ## What it does not prove
 *
 * That the agent has read any of it, or that any record exists that it may
 * see. A path here is a possibility: the type is permitted and the type has
 * been written. Whether those are the same records depends on tags, which are
 * a per-record fact this picture does not carry.
 */

/** Column geometry. Everything else is derived from these. */
const NODE_W = 190;
const NODE_H = 38;
const GAP_Y = 16;
const GAP_X = 92;
const PAD = 12;

const COLUMNS = ["connector", "type", "app", "agent"];

const HEADING = {
  connector: "Brings data in",
  type: "What kind",
  app: "Which app may read it",
  agent: "Which agent runs there",
};

export default function Wiring({ get, onEffective }) {
  const [apps, setApps] = useState(null);
  const [writers, setWriters] = useState(null);
  const [failure, setFailure] = useState(null);
  const [hot, setHot] = useState(null);

  /**
   * SVG paint is not `sx`.
   *
   * `sx={{ fill: "background.paper" }}` looks like every other colour in this
   * repo and is not one: `sx` resolves palette paths for the properties it
   * knows — `color`, `bgcolor`, `borderColor` — and passes anything else
   * through as a literal. `fill: "background.paper"` reaches the DOM as
   * exactly that string, which is not a colour, so every node rendered as a
   * solid black box and every edge vanished.
   *
   * `theme.vars.palette.*` are `var(--kernel-palette-…)` references rather
   * than resolved values, which is the property that matters here: the
   * browser resolves them, so the diagram follows a scheme change without
   * this component knowing one happened.
   */
  const theme = useTheme();
  const paint = theme.vars?.palette || theme.palette;
  const ink = {
    paper: paint.background.paper,
    text: paint.text.primary,
    quiet: paint.text.secondary,
    line: paint.divider,
    brand: paint.primary.main,
    agent: paint.kernel?.unattended || paint.primary.main,
  };

  const load = useCallback(async () => {
    setFailure(null);
    try {
      // Both already permitted to this app and both already read by the
      // chart next door. Nothing was widened to draw this.
      const [register, sources] = await Promise.all([
        get("/apps"),
        get("/connectors"),
      ]);
      setApps((register.apps || []).filter((a) => !a.unparseable));
      setWriters(sources.writers || []);
    } catch (e) {
      setFailure(e);
    }
  }, [get]);

  useEffect(() => {
    load();
    onEffective?.(null);
  }, [load, onEffective]);

  const graph = useMemo(() => {
    if (!apps || !writers) return null;

    const connectors = writers.filter((w) => w.kind === "connector");

    // Only the apps that declare an agent. This screen is named after agents,
    // and drawing the other nine apps would answer a different question in
    // the same picture.
    const staffed = apps.filter((a) => (a.agents || []).length);

    // `service_reads`, not `reads`. An agent runs with nobody signed in, so
    // the unattended half is the one in force — drawing `reads` would show a
    // ceiling that does not apply to the thing this screen is about.
    const readsOf = (app) => (app.service_reads || []).map((g) => g.type);

    const written = new Set();
    for (const c of connectors) {
      for (const type of Object.keys(c.objects || {})) written.add(type);
    }
    const read = new Set();
    for (const app of staffed) for (const t of readsOf(app)) read.add(t);

    // A type appears if it is on some path — written by a connector, or
    // readable by an agent's app. A type that is neither is a type this
    // picture has nothing to say about.
    const types = [...new Set([...written, ...read])].sort();

    const nodes = [];
    const edges = [];
    const push = (kind, id, label, meta) => {
      nodes.push({ kind, id: `${kind}:${id}`, label, meta });
      return `${kind}:${id}`;
    };

    for (const c of connectors) {
      const id = push("connector", c.source, c.source, {
        total: c.total || 0,
        types: Object.keys(c.objects || {}),
      });
      for (const [type, n] of Object.entries(c.objects || {})) {
        if (!types.includes(type)) continue;
        edges.push({ from: id, to: `type:${type}`, kind: "writes", n });
      }
    }

    for (const type of types) push("type", type, type, {});

    for (const app of staffed) {
      const id = push("app", app.app_type, app.app_type, {
        reads: readsOf(app),
        actions: app.actions || [],
      });
      for (const type of readsOf(app)) {
        edges.push({ from: `type:${type}`, to: id, kind: "reads" });
      }
      for (const agent of app.agents || []) {
        const a = push("agent", `${app.app_type}:${agent.name}`, agent.name, {
          does: agent.does,
          division: agent.division,
        });
        edges.push({ from: id, to: a, kind: "declares" });
      }
    }

    // Lay the columns out. Positions are computed rather than measured
    // because an SVG that waits for the DOM to settle before it can draw a
    // line is an SVG that flickers on every render.
    const byColumn = COLUMNS.map((kind) => nodes.filter((n) => n.kind === kind));
    const tallest = Math.max(...byColumn.map((c) => c.length), 1);
    const height = tallest * NODE_H + (tallest - 1) * GAP_Y + PAD * 2;
    const width = COLUMNS.length * NODE_W + (COLUMNS.length - 1) * GAP_X;

    const at = {};
    byColumn.forEach((column, c) => {
      const span = column.length * NODE_H + (column.length - 1) * GAP_Y;
      const top = (height - span) / 2;
      column.forEach((node, i) => {
        at[node.id] = {
          x: c * (NODE_W + GAP_X),
          y: top + i * (NODE_H + GAP_Y),
        };
      });
    });

    /**
     * What lights up when one node is picked: its upstream and its
     * downstream, and nothing sideways.
     *
     * ## Why this is not a connected component
     *
     * It was, and the result was that hovering anything lit the entire
     * diagram. These columns are joined through shared types — `xero` writes
     * `Person` and so does `google`, `Invoice` and `Organization` both come
     * from `xero` — so the whole thing is one component and "everything is
     * related to everything" is a true and completely useless answer.
     *
     * The question a person is asking when they point at `receivables-review`
     * is *what feeds this*, which is a walk **backwards only**: its app, the
     * types that app may read, the connectors that wrote those types. It is
     * emphatically not "and then everything else those connectors also
     * wrote", which is how the component walk got from one agent to the other
     * agent in two hops.
     *
     * Pointing at a connector asks the mirror question — *what does this
     * reach* — and that is the same walk forwards. Both run from the node
     * picked, and neither turns around at the end.
     */
    const touching = (id) => {
      if (!id) return null;
      const keep = new Set([id]);

      const walk = (start, next) => {
        const queue = [start];
        while (queue.length) {
          const here = queue.pop();
          for (const e of edges) {
            const [a, b] = next(e);
            if (a !== here || keep.has(b)) continue;
            keep.add(b);
            queue.push(b);
          }
        }
      };

      walk(id, (e) => [e.from, e.to]);
      walk(id, (e) => [e.to, e.from]);
      return keep;
    };

    return { nodes, edges, at, width, height, byColumn, touching, types };
  }, [apps, writers]);

  if (failure) {
    return <Refusal failure={failure} actor="Agent OS" onRetry={load} />;
  }
  if (!graph) return <Loading label="Tracing where the data comes from" />;

  const lit = graph.touching(hot);
  const on = (id) => !lit || lit.has(id);

  const agents = graph.byColumn[3];
  const fed = new Set();
  for (const agent of agents) {
    const reach = graph.touching(agent.id);
    for (const id of reach) if (id.startsWith("connector:")) fed.add(id);
  }
  const connectors = graph.byColumn[0];

  return (
    <Stack spacing={3}>
      <Headline
        eyebrow="Reaching every declared agent"
        figure={`${fed.size}/${connectors.length}`}
        caption={
          connectors.length - fed.size === 0
            ? "connectors feed an agent"
            : `connectors feed an agent — ${connectors.length - fed.size} write${
                connectors.length - fed.size === 1 ? "s" : ""
              } nothing any agent may read`
        }
        basis="Derived by joining three facts on a type: what a connector has written, what an app's service_reads permit, and which agents its manifest declares. It is a statement about reach and not about wiring — nothing connects a connector to an agent, and nobody configured a pipeline."
      />

      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, minWidth: 0 }}>
        <Stack
          direction="row"
          spacing={2}
          sx={{ justifyContent: "space-between", flexWrap: "wrap", mb: 2 }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h2">How the data reaches them</Typography>
            <Typography variant="body2" color="text.secondary">
              Hover anything to follow its path. Two of these hops are
              declared and one is derived.
            </Typography>
          </Box>
          <Stack direction="row" spacing={2} sx={{ flexWrap: "wrap", alignItems: "center" }}>
            <Stack direction="row" spacing={0.75} sx={{ alignItems: "center" }}>
              <Box sx={{ width: 22, height: 0, borderTop: "2px solid", borderColor: "primary.main" }} />
              <Typography variant="caption" color="text.secondary">
                declared
              </Typography>
            </Stack>
            <Stack direction="row" spacing={0.75} sx={{ alignItems: "center" }}>
              <Box
                sx={{
                  width: 22,
                  height: 0,
                  borderTop: "2px dashed",
                  borderColor: "text.secondary",
                }}
              />
              <Typography variant="caption" color="text.secondary">
                observed — what has been written
              </Typography>
            </Stack>
          </Stack>
        </Stack>

        {/* Its own scroll container. Four columns of 190 is wider than a
            phone and the page body never scrolls sideways. */}
        <Box sx={{ overflowX: "auto", pb: 1 }}>
          <Box
            component="svg"
            viewBox={`0 0 ${graph.width} ${graph.height}`}
            width={graph.width}
            height={graph.height}
            role="img"
            aria-label={`A diagram of ${graph.byColumn[0].length} connectors, ${graph.types.length} record types, ${graph.byColumn[2].length} apps and ${agents.length} agents, and the paths between them. The same information is written out below the diagram.`}
            sx={{ display: "block", maxWidth: "none", minWidth: graph.width }}
          >
            {graph.edges.map((edge, i) => {
              const a = graph.at[edge.from];
              const b = graph.at[edge.to];
              if (!a || !b) return null;
              const x1 = a.x + NODE_W;
              const y1 = a.y + NODE_H / 2;
              const x2 = b.x;
              const y2 = b.y + NODE_H / 2;
              const mid = (x1 + x2) / 2;
              const on2 = on(edge.from) && on(edge.to);
              return (
                <path
                  key={i}
                  d={`M ${x1} ${y1} C ${mid} ${y1}, ${mid} ${y2}, ${x2} ${y2}`}
                  fill="none"
                  stroke={edge.kind === "writes" ? ink.quiet : ink.brand}
                  strokeWidth={on2 && lit ? 2 : 1.25}
                  // Observed rather than declared, and drawn as such. The
                  // `writes` hop is the only one that is a count of what has
                  // happened rather than a decision somebody made.
                  strokeDasharray={edge.kind === "writes" ? "4 3" : undefined}
                  opacity={on2 ? (lit ? 0.9 : 0.45) : 0.08}
                  style={{ transition: "opacity 140ms, stroke-width 140ms" }}
                />
              );
            })}

            {graph.nodes.map((node) => {
              const p = graph.at[node.id];
              const alive = on(node.id);
              return (
                <g
                  key={node.id}
                  onMouseEnter={() => setHot(node.id)}
                  onMouseLeave={() => setHot(null)}
                  onFocus={() => setHot(node.id)}
                  onBlur={() => setHot(null)}
                  tabIndex={0}
                  role="button"
                  aria-label={`${node.kind} ${node.label}`}
                  opacity={alive ? 1 : 0.25}
                  style={{ cursor: "pointer", transition: "opacity 140ms" }}
                >
                  <rect
                    x={p.x}
                    y={p.y}
                    width={NODE_W}
                    height={NODE_H}
                    rx={7}
                    fill={ink.paper}
                    stroke={
                      node.kind === "agent"
                        ? ink.agent
                        : node.kind === "connector"
                          ? ink.quiet
                          : ink.line
                    }
                    strokeWidth={hot === node.id ? 2 : 1}
                  />
                  <text
                    x={p.x + 12}
                    y={p.y + NODE_H / 2 + 4}
                    fill={ink.text}
                    fontSize={12.5}
                    fontFamily={
                      node.kind === "type" ? "inherit" : "ui-monospace, monospace"
                    }
                    fontWeight={node.kind === "agent" ? 600 : 400}
                  >
                    {node.label}
                  </text>
                </g>
              );
            })}
          </Box>
        </Box>

        <Stack direction="row" spacing={3} sx={{ flexWrap: "wrap", rowGap: 1, mt: 1 }}>
          {COLUMNS.map((kind, i) => (
            <Box key={kind} sx={{ minWidth: 0, flex: "1 1 150px" }}>
              <Typography variant="overline" color="text.secondary" sx={{ display: "block" }}>
                {HEADING[kind]}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {graph.byColumn[i].length}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Paper>

      {/* The same information, written out. A picture that is the only way to
          read a fact is a fact half the audience does not have — and this one
          is a diagram with no text alternative unless somebody writes it. */}
      <Disclose
        align="row"
        label="The same thing in sentences"
        count={graph.byColumn[3].length + graph.byColumn[0].length}
      >
        <Stack spacing={1.5}>
          {graph.byColumn[3].map((agent) => {
            const reach = graph.touching(agent.id);
            const from = [...reach]
              .filter((id) => id.startsWith("connector:"))
              .map((id) => id.slice("connector:".length));
            const types = [...reach]
              .filter((id) => id.startsWith("type:"))
              .map((id) => id.slice("type:".length));
            return (
              <Typography key={agent.id} variant="body2" color="text.secondary">
                <Box component="span" sx={{ fontWeight: 600 }}>
                  {agent.label}
                </Box>{" "}
                may read {types.join(", ") || "nothing"} with nobody signed in.{" "}
                {from.length
                  ? `Records of ${
                      types.length === 1 ? "that type" : "those types"
                    } have been written by ${from.join(", ")} — so it can reach data ${
                      from.length === 1 ? "that connector" : "those connectors"
                    } brought in. Nothing connects them; they meet at the type.`
                  : "No connector has written a record of any type it may read."}
              </Typography>
            );
          })}
          {graph.byColumn[0]
            .filter((c) => !fed.has(c.id))
            .map((c) => (
              <Typography key={c.id} variant="body2" color="text.secondary">
                <Box component="span" sx={{ fontWeight: 600 }}>
                  {c.label}
                </Box>{" "}
                writes {c.meta.types.join(", ") || "nothing"}, and no agent
                declares a service read for{" "}
                {c.meta.types.length === 1 ? "that type" : "any of those types"}.
                That is a fact about what is declared, not a fault.
              </Typography>
            ))}
        </Stack>
      </Disclose>
    </Stack>
  );
}
